import { ref, watch, computed } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth';
import type { CompanyRef as CompanyRefType, ExpirationPeriod } from '../domain/types/Company';
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider';
import { FirestoreCompanyConfigRepository } from '@/infrastructure/repositories/FirestoreCompanyConfigRepository';
import { doc, getDoc, getFirestore, updateDoc, setDoc, deleteField, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { setFormatSettings } from '@/composables/useFormat';
import type {
  CompanySettingsModule, 
  CompanySettingsSection, 
  CompanySetting 
} from '../domain/types/configs/CompanySettingsModule';
import type { CompanyRole, ModulePermissions } from '../domain/types/Role';
import { canEmailJoinCompany } from '@/utils/emailDomains';
import type { DefaultDetailTabs, DefaultListViews } from '@/interfaces/user-preferences';

const STORAGE_KEY = 'selectedCompanyId';

interface SettingsSection {
  id: string
  name: string
  description: string
  isEnabled: boolean
  settings: Record<string, any>
}

export const useCompanyStore = defineStore('company', () => {
  const companyRepository = new FirestoreCompanyConfigRepository();
  // Usar useLocalStorage para manejar automáticamente la sincronización
  const selectedCompanyId = useLocalStorage<string | null>(STORAGE_KEY, null);
  const authStore = useAuthStore();
  
  // Estado para el superadmin
  const allCompanies = ref<CompanyRefType[]>([]);
  const isLoadingAllCompanies = ref(false);
  
  // Estado para datos completos de la compañía actual
  const currentCompanyData = ref<any>(null);
  const isLoadingCompanyData = ref(false);
  // Pestaña con la que abre el detalle, elegida por el usuario (KILA-506)
  const userDefaultDetailTabs = ref<DefaultDetailTabs>({});
  // Vista con estrella de cada listado, por usuario (KILA-506)
  const userDefaultListViews = ref<DefaultListViews>({});
  // Carga de preferencias del usuario en vuelo o hecha, por compañía/usuario
  let userPrefsLoad: Promise<void> | null = null;
  let userPrefsLoadKey: string | null = null;
  // Carga en vuelo, para que el guard del router y el watch no dupliquen el getDoc
  let companyDataLoad: Promise<any> | null = null;
  let companyDataLoadId: string | null = null;
  // Última compañía ya intentada, exista o no: el guard corre en cada navegación
  // y sin esto una compañía sin documento se releería una vez por ruta.
  let companyDataAttemptedId: string | null = null;
  
  // Settings state
  const settingsSections = ref<Record<string, SettingsSection>>({
    'general': {
      id: 'general',
      name: 'General',
      description: 'Configuración general del proyecto',
      isEnabled: true,
      settings: {}
    },
    'cloud-messaging': {
      id: 'cloud-messaging',
      name: 'Cloud Messaging',
      description: 'Configuración de mensajería en la nube',
      isEnabled: true,
      settings: {}
    },
    'integraciones': {
      id: 'integraciones',
      name: 'Integraciones',
      description: 'Gestión de integraciones con servicios externos',
      isEnabled: true,
      settings: {}
    },
    'cuentas-servicio': {
      id: 'cuentas-servicio',
      name: 'Cuentas de servicio',
      description: 'Administración de cuentas de servicio',
      isEnabled: true,
      settings: {}
    },
    'privacidad': {
      id: 'privacidad',
      name: 'Privacidad de los datos',
      description: 'Configuración de privacidad y protección de datos',
      isEnabled: true,
      settings: {}
    },
    'usuarios-permisos': {
      id: 'usuarios-permisos',
      name: 'Usuarios y permisos',
      description: 'Gestión de usuarios y permisos del sistema',
      isEnabled: true,
      settings: {}
    },
    'data-import': {
      id: 'data-import',
      name: 'Importar Datos',
      description: 'Importación masiva de embarques, cotizaciones y órdenes de compra',
      isEnabled: true,
      settings: {}
    }
  });
  const hasUnsavedChanges = ref(false);


  // Computed para verificar si el usuario es superadmin
  const isSuperAdmin = computed(() => (authStore.user?.role as any) === 'superadmin');

  // Función para cargar todas las compañías (solo para superadmin)
  const loadAllCompanies = async () => {
    if (!isSuperAdmin.value) {
      console.warn('loadAllCompanies: Solo permitido para superadmin');
      return;
    }

    isLoadingAllCompanies.value = true;
    try {
      allCompanies.value = await companyRepository.getAllCompanies();
    } catch (error) {
      console.error('❌ Error loading all companies:', error);
      throw error;
    } finally {
      isLoadingAllCompanies.value = false;
    }
  };

  // Computed para obtener la lista de compañías disponibles
  // El superadmin ve sus compañías asociadas más las que traiga la lectura de
  // toda la colección: si esa lectura falla o llega vacía, no se queda sin selector.
  const availableCompanies = computed(() => {
    const ownCompanies = authStore.user?.companiesRef || [];
    if (!isSuperAdmin.value) return ownCompanies;
    const ownIds = new Set(ownCompanies.map(company => company.id));
    return [...ownCompanies, ...allCompanies.value.filter(company => !ownIds.has(company.id))];
  });

  // Watch for user changes to reset selected company
  watch(() => authStore.user, async (newUser) => {
    
    if (newUser && authStore.isInitialized) {
      // Si es superadmin, cargar todas las compañías
      if ((newUser.role as any) === 'superadmin') {
        try {
          await loadAllCompanies();
        } catch (error) {
          console.error('❌ Error in loadAllCompanies:', error);
        }
        // Si no hay compañía seleccionada y hay compañías disponibles, seleccionar la primera
        if (!selectedCompanyId.value && availableCompanies.value.length > 0) {
          selectedCompanyId.value = availableCompanies.value[0].id;
        }
      } else if (newUser?.companiesRef?.length > 0) {
        // Lógica normal para usuarios regulares
        if (selectedCompanyId.value && 
            newUser.companiesRef.some(company => company.id === selectedCompanyId.value)) {
          // Keep the stored company
        } else {
          // useLocalStorage se encarga automáticamente de persistir el valor
          selectedCompanyId.value = newUser.companiesRef[0].id;
        }
      }
    }
  }, { immediate: true });
  
  // Recargar catálogos cuando cambia la compañía
  watch(selectedCompanyId, async (newId, oldId) => {
    if (newId && newId !== oldId && oldId !== null) {
      try {
        // La configuración de solo vista es por compañía y decide a qué módulos
        // entra un viewer: hay que descartar la de la compañía anterior antes de
        // que se vuelvan a resolver permisos.
        const { invalidateViewerConfigCache, ensureViewerConfigLoaded } = await import('@/composables/useViewerConfig')
        invalidateViewerConfigCache()
        await ensureViewerConfigLoaded()

        const { useCatalogsStore } = await import('./catalogs')
        const catalogsStore = useCatalogsStore()
        await catalogsStore.loadConfigs(newId)
        const { useDocumentTypes } = await import('@/composables/useDocumentTypes')
        await useDocumentTypes().loadCustomTypes(newId, true)
        const { useCalculatedCharges } = await import('@/composables/useCalculatedCharges')
        await useCalculatedCharges().loadTemplates(newId, true)
      } catch (e) {
        console.error('[CompanyStore] Error recargando catálogos:', e)
      }
    }
  })

  const setSelectedCompany = (companyId: string | null) => {
    // Para superadmin, permitir cualquier compañía
    if (isSuperAdmin.value) {
      selectedCompanyId.value = companyId;
      return;
    }
    
    // Para usuarios normales, verificar que pertenezca a sus compañías
    if (companyId === null || authStore.user?.companiesRef?.some(company => company.id === companyId)) {
      selectedCompanyId.value = companyId;
    }
  };

  const getSelectedCompany = (): any => {
    if (selectedCompanyId.value === null) {
      return null;
    }
    if (isSuperAdmin.value) {
      return availableCompanies.value.find(
        company => company.id === selectedCompanyId.value
      );
    }
    return authStore.user?.companiesRef?.find(
      company => company.id === selectedCompanyId.value
    );
  };

  async function generateShareLink(
    companyId: string,
    expiration: ExpirationPeriod,
    role: CompanyRole,
    permissions?: ModulePermissions
  ) {
    try {
      return await companyRepository.generateShareLink(companyId, {
        expiration,
        role,
        permissions,
        createdBy: authStore.user?.uid
      });
    } catch (error) {
      console.error('Error generating share link:', error);
      throw error;
    }
  }

  async function validateInviteLink(inviteId: string) {
    try {
      return await companyRepository.validateInviteLink(inviteId);
    } catch (error) {
      console.error('Error validating invite:', error);
      throw error;
    }
  }

  // Settings management
  const getSettingsModules = (): SettingsSection[] => {
    return Object.values(settingsSections.value).filter(section => section.isEnabled)
  };

  const getSettingsSections = () => {
    return settingsSections.value
  };

  const getSettings = (sectionId: string) => {
    return settingsSections.value[sectionId]?.settings || {}
  };

  const updateSetting = (sectionId: string, key: string, value: any) => {
    if (settingsSections.value[sectionId]) {
      settingsSections.value[sectionId].settings[key] = value
    }
    hasUnsavedChanges.value = true;
  };

  const saveSettingsChanges = async (sectionId: string) => {
    try {
      // Implementar la lógica para guardar los cambios en el backend
      hasUnsavedChanges.value = false;
      return true;
    } catch (error) {
      console.error('Error saving settings:', error);
      return false;
    }
  };

  const resetSectionToDefault = async (sectionId: string) => {
    try {
      if (settingsSections.value[sectionId]) {
        settingsSections.value[sectionId].settings = {}
      }
      hasUnsavedChanges.value = true;
      return true;
    } catch (error) {
      console.error('Error resetting section:', error);
      return false;
    }
  };

  // Cargar datos completos de la compañía desde Firestore
  const loadCompanyData = async (companyId: string) => {
    if (!companyId) {
      currentCompanyData.value = null;
      return null;
    }

    isLoadingCompanyData.value = true;
    try {
      const db = getFirestore(FirebaseProvider.getInstance().getApp());
      const companyDocRef = doc(db, 'Companies', companyId);
      const companyDoc = await getDoc(companyDocRef);
      
      if (companyDoc.exists()) {
        currentCompanyData.value = {
          id: companyDoc.id,
          ...companyDoc.data()
        };
        return currentCompanyData.value;
      } else {
        currentCompanyData.value = null;
        return null;
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      currentCompanyData.value = null;
      return null;
    } finally {
      isLoadingCompanyData.value = false;
    }
  };

  // Carga el formato de la compañía y le aplica encima el override del usuario.
  // La config de compañía viene gratis en currentCompanyData (0 lecturas extra);
  // solo el override del usuario cuesta una lectura.
  const loadFormatSettings = async (companyId: string) => {
    setFormatSettings(currentCompanyData.value?.formatSettings, companyId);
    userDefaultDetailTabs.value = {};
    userDefaultListViews.value = {};

    const userId = authStore.user?.uid;
    if (!userId) return;

    try {
      const db = getFirestore(FirebaseProvider.getInstance().getApp());
      const userPrefsRef = doc(db, `Companies/${companyId}/userPreferences/${userId}`);
      const userPrefsSnap = await getDoc(userPrefsRef);
      // Si mientras tanto cambió la compañía o el usuario, esta respuesta ya no aplica
      if (companyId !== selectedCompanyId.value || userId !== authStore.user?.uid) return;
      const userFormat = userPrefsSnap.data()?.formatSettings;
      userDefaultDetailTabs.value = userPrefsSnap.data()?.defaultDetailTabs ?? {};
      userDefaultListViews.value = userPrefsSnap.data()?.defaultListViews ?? {};
      if (userFormat) {
        setFormatSettings({ ...currentCompanyData.value?.formatSettings, ...userFormat }, companyId);
      }
    } catch (error) {
      console.error('Error loading user format override:', error);
    }
  };

  /**
   * Datos de la compañía disponibles, cargándolos si hace falta. El guard del
   * router los necesita para decidir el acceso a un módulo, y en un arranque en
   * frío llega antes de que el watch de abajo termine: sin esperar aquí, la
   * primera navegación se resolvía con los flags todavía en null y dejaba pasar
   * a un módulo que el usuario no tiene.
   *
   * Las llamadas concurrentes comparten la promesa en vuelo para no repetir el
   * getDoc de la compañía.
   */
  const ensureCompanyDataLoaded = async (): Promise<any> => {
    const companyId = selectedCompanyId.value;
    if (!companyId) return null;
    if (currentCompanyData.value?.id === companyId) return currentCompanyData.value;
    if (companyDataAttemptedId === companyId && !companyDataLoad) return currentCompanyData.value;

    if (!companyDataLoad || companyDataLoadId !== companyId) {
      companyDataLoadId = companyId;
      companyDataLoad = loadCompanyData(companyId).finally(() => {
        companyDataAttemptedId = companyId;
        companyDataLoad = null;
        companyDataLoadId = null;
      });
    }
    return companyDataLoad;
  };

  /**
   * Preferencias del usuario actual cargadas. Los listados inicializan en paralelo
   * con el watch de abajo y necesitan la vista con estrella antes de aplicar
   * columnas: comparten la misma carga (un solo getDoc) por compañía/usuario.
   */
  const ensureUserPreferencesLoaded = (): Promise<void> => {
    const key = `${selectedCompanyId.value}/${authStore.user?.uid}`;
    if (!userPrefsLoad || userPrefsLoadKey !== key) {
      userPrefsLoadKey = key;
      userPrefsLoad = (async () => {
        const companyId = selectedCompanyId.value;
        if (!companyId) return;
        await ensureCompanyDataLoaded();
        if (companyId !== selectedCompanyId.value) return;
        await loadFormatSettings(companyId);
      })();
    }
    return userPrefsLoad;
  };

  /** Marca (presetId) o quita (null) la vista con estrella de un listado. */
  const setUserDefaultListView = async (module: keyof DefaultListViews, presetId: string | null) => {
    const companyId = selectedCompanyId.value;
    const userId = authStore.user?.uid;
    if (!companyId || !userId) throw new Error('Sin compañía o usuario');
    const db = getFirestore(FirebaseProvider.getInstance().getApp());
    const ref = doc(db, `Companies/${companyId}/userPreferences/${userId}`);
    const now = Timestamp.now();
    try {
      await updateDoc(ref, { [`defaultListViews.${module}`]: presetId ?? deleteField(), updatedAt: now });
    } catch (e: any) {
      // Sin doc todavía: se crea con createdAt. Probar update primero evita una lectura.
      if (e?.code !== 'not-found') throw e;
      await setDoc(
        ref,
        { userId, companyId, defaultListViews: { [module]: presetId ?? deleteField() }, createdAt: now, updatedAt: now },
        { merge: true }
      );
    }
    const next = { ...userDefaultListViews.value };
    if (presetId) next[module] = presetId;
    else delete next[module];
    userDefaultListViews.value = next;
  };

  // Watch para cargar datos completos cuando cambie la compañía seleccionada, y
  // las preferencias del usuario también cuando cambie quién está autenticado
  // (login/logout en la misma compañía, o authStore.user aún null al arrancar).
  watch([selectedCompanyId, () => authStore.user?.uid], async ([newCompanyId]) => {
    if (newCompanyId) {
      await ensureUserPreferencesLoaded();
    } else {
      currentCompanyData.value = null;
      userDefaultDetailTabs.value = {};
      userDefaultListViews.value = {};
      userPrefsLoad = null;
    }
  }, { immediate: true });

  /**
   * La compañía activa se recuerda en localStorage, así que el chequeo de dominios
   * del login no alcanza: si la compañía cargada ya no acepta este correo se vuelve
   * a la que sí validó el login y, si tampoco sirve, se queda sin compañía activa.
   * El superadmin es soporte de la plataforma y entra a cualquiera.
   */
  watch([currentCompanyData, () => authStore.user?.email], ([company, email]) => {
    if (!company || !email || authStore.user?.role === 'superadmin') return;
    const emailVerified = getAuth(FirebaseProvider.getInstance().getApp()).currentUser?.emailVerified ?? false;
    if (canEmailJoinCompany(email, company.allowedEmailDomains, emailVerified)) return;

    const validatedCompanyId = authStore.user?.companyId;
    selectedCompanyId.value =
      validatedCompanyId && validatedCompanyId !== company.id ? validatedCompanyId : null;
  });

  return {
    selectedCompanyId,
    setSelectedCompany,
    getSelectedCompany,
    generateShareLink,
    validateInviteLink,
    // Settings functions
    getSettingsModules,
    getSettingsSections,
    getSettings,
    updateSetting,
    saveSettingsChanges,
    resetSectionToDefault,
    hasUnsavedChanges,
    // SuperAdmin functions
    isSuperAdmin,
    availableCompanies,
    allCompanies,
    isLoadingAllCompanies,
    loadAllCompanies,
    // Company full data
    currentCompanyData,
    isLoadingCompanyData,
    loadCompanyData,
    ensureCompanyDataLoaded,
    userDefaultDetailTabs,
    userDefaultListViews,
    ensureUserPreferencesLoaded,
    setUserDefaultListView,
    // Billing flags
    showShipmentRequest: computed(() => currentCompanyData.value?.showShipmentRequest === true),
    showAutomaticEmails: computed(() => currentCompanyData.value?.showAutomaticEmails === true),
    showSatlockTracking: computed(() => currentCompanyData.value?.showSatlockTracking === true),
    showTvBoard: computed(() => currentCompanyData.value?.showTvBoard === true),
    showSharePointFolders: computed(() => currentCompanyData.value?.showSharePointFolders === true),
    showRelatedEmails: computed(() => currentCompanyData.value?.showRelatedEmails === true),
  };
});